import { Suspense, ReactNode } from 'react'
import { lazyWithReload } from '@utils/lazyWithReload'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { AuthProvider } from '@contexts/AuthContext'
import { ThemeProvider } from '@contexts/ThemeContext'
import { TimerProvider } from '@contexts/TimerContext'
import { SystemProvider } from '@contexts/SystemContext'
import { NotificationProvider } from '@contexts/NotificationContext'
import { OfficePresenceProvider } from '@contexts/OfficePresenceContext'
import { LevelMapProvider } from '@contexts/LevelMapContext'
// Layout、Auth 相關元件保留靜態 import（首屏必要）
import Layout from '@components/Layout/Layout'
import ProtectedRoute from '@components/Auth/ProtectedRoute'

// PWA Components（輕量，保留靜態 import）
import { InstallPrompt } from '@components/PWA/InstallPrompt'
import { OfflineIndicator } from '@components/PWA/OfflineIndicator'
import { RouteErrorBoundary } from '@components/ErrorBoundary/RouteErrorBoundary'

// Eager pages：首屏 (Timer) / 登入 / 404 — 體積小且常用，eager 載入避免 Suspense flash
import LoginPage from '@pages/Auth/LoginPage'
import TimerPage from '@pages/Timer/TimerPage'
import NotFoundPage from '@pages/NotFound/NotFoundPage'

// Lazy pages：其餘頁面切 chunk，導航時才下載。
// lazyWithReload：chunk 404（改版部署後舊 index 引用舊 chunk）時自動整頁重載一次自救
const ProjectsPage = lazyWithReload(() => import('@pages/Projects/ProjectsPage'))
const ProjectDetailPage = lazyWithReload(() => import('@pages/Projects/ProjectDetailPage'))
const TimesheetsPage = lazyWithReload(() => import('@pages/Timesheets/TimesheetsPage'))
const TimesheetDetailPage = lazyWithReload(() => import('@pages/Timesheets/TimesheetDetailPage'))
const TimesheetEditPage = lazyWithReload(() => import('@pages/Timesheets/TimesheetEditPage'))
const SalariesPage = lazyWithReload(() => import('@pages/Salaries/SalariesPage'))
const SalaryDetailPage = lazyWithReload(() => import('@pages/Salaries/SalaryDetailPage'))
const BenefitsPage = lazyWithReload(() => import('@pages/Benefits/BenefitsPage'))
const ApprovalsPage = lazyWithReload(() => import('@pages/Approvals/ApprovalsPage'))
const ApprovalDetailPage = lazyWithReload(() => import('@pages/Approvals/ApprovalDetailPage'))
const ReportsPage = lazyWithReload(() => import('@pages/Reports/ReportsPage'))
const SettingsPage = lazyWithReload(() => import('@pages/Settings/SettingsPage'))
const UsersPage = lazyWithReload(() => import('@pages/Users/UsersPage'))
const NotificationsPage = lazyWithReload(() => import('@pages/Notifications/NotificationsPage'))
const PartnerMapPage = lazyWithReload(() => import('@pages/PartnerMap/PartnerMapPage'))
const SalaryDraftsPage = lazyWithReload(() => import('@pages/Admin/SalaryDraftsPage'))
const EmployerCostPage = lazyWithReload(() => import('@pages/Admin/EmployerCostPage'))
const BenefitApprovalsPage = lazyWithReload(() => import('@pages/Admin/BenefitApprovalsPage'))
const BenefitRewardsPage = lazyWithReload(() => import('@pages/Admin/BenefitRewards/BenefitRewardsPage'))
const AllowancesPage = lazyWithReload(() => import('@pages/Admin/Allowances/AllowancesPage'))
const ExpensesPage = lazyWithReload(() => import('@pages/Expenses/ExpensesPage'))
const ExpenseApprovalsPage = lazyWithReload(() => import('@pages/Admin/ExpenseApprovalsPage'))
const PerformancePage = lazyWithReload(() => import('@pages/Performance/PerformancePage'))
const DocumentsPage = lazyWithReload(() => import('@pages/Documents/DocumentsPage'))
const DocumentSignPage = lazyWithReload(() => import('@pages/Documents/DocumentSignPage'))
const DocumentTemplatesPage = lazyWithReload(() => import('@pages/Admin/Documents/DocumentTemplatesPage'))
const DocumentSignatureApprovalsPage = lazyWithReload(() => import('@pages/Admin/Documents/DocumentSignatureApprovalsPage'))
const MyGoalsPage = lazyWithReload(() => import('@pages/Goals/MyGoalsPage'))
const GoalsAdminPage = lazyWithReload(() => import('@pages/Admin/Goals/GoalsAdminPage'))
const AdminGoalDetailPage = lazyWithReload(() => import('@pages/Admin/Goals/AdminGoalDetailPage'))
const PerformanceAdminPage = lazyWithReload(() => import('@pages/Admin/Performance/PerformanceAdminPage'))
const BonusPoolsListPage = lazyWithReload(() => import('@pages/Admin/BonusPools/BonusPoolsListPage'))
const BonusPoolDetailPage = lazyWithReload(() => import('@pages/Admin/BonusPools/BonusPoolDetailPage'))
const MyBonusPoolPage = lazyWithReload(() => import('@pages/BonusPool/MyBonusPoolPage'))
const MyBonusPoolDetailPage = lazyWithReload(() => import('@pages/BonusPool/MyBonusPoolDetailPage'))
const OfficePage = lazyWithReload(() => import('@pages/Office/OfficePage'))
const AnnouncementsPage = lazyWithReload(() => import('@pages/Announcements/AnnouncementsPage'))
const WikiHomePage = lazyWithReload(() => import('@pages/Wiki/WikiHomePage'))
const WikiArticlePage = lazyWithReload(() => import('@pages/Wiki/WikiArticlePage'))
const WikiEditPage = lazyWithReload(() => import('@pages/Wiki/WikiEditPage'))
const WikiAdminPage = lazyWithReload(() => import('@pages/Admin/Wiki/WikiAdminPage'))
const LevelMapsPage = lazyWithReload(() => import('@pages/Admin/LevelMaps/LevelMapsPage'))
const AnnouncementAdminPage = lazyWithReload(() => import('@pages/Admin/Announcements/AnnouncementAdminPage'))

// Suspense fallback：放在 Layout 內 → 保留側邊欄/Header，只有內容區換 spinner
const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]" aria-busy="true" aria-live="polite">
    <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
  </div>
)

// 每條 route 的 ErrorBoundary + Suspense 包裝。
// ErrorBoundary 放外層：lazy chunk 抓不到 / page 元件 throw 時都會被攔到顯示恢復 UI，
// 不會把整顆 React tree 弄壞而導致「按了第 6 頁後續全部卡住」。
// RouteErrorBoundary 自己會用 location.pathname 當 key → 切到別頁就自動 reset。
const Lazy = ({ children }: { children: ReactNode }) => (
  <RouteErrorBoundary>
    <Suspense fallback={<PageFallback />}>{children}</Suspense>
  </RouteErrorBoundary>
)

function App() {
  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''}>
    <ThemeProvider>
      <SystemProvider>
        <AuthProvider>
          <NotificationProvider>
            <TimerProvider>
              <OfficePresenceProvider>
                <LevelMapProvider>
              {/* Toast Notifications - 放在最外層確保 z-index 正確運作 */}
            <Toaster
              position="top-right"
              containerStyle={{
                zIndex: 99999,
              }}
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#ffffff',
                  color: '#1e293b',
                  border: '2px solid #fef3c7',
                  borderRadius: '12px',
                  boxShadow: '0 4px 25px -5px rgba(245, 158, 11, 0.15), 0 10px 30px -5px rgba(245, 158, 11, 0.08)',
                  fontWeight: '500',
                  zIndex: 99999,
                },
                success: {
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#ffffff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#ffffff',
                  },
                },
              }}
            />
            <Router>
          <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected Routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><TimerPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/timer" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><TimerPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects" element={
                <ProtectedRoute requiredRole={['ADMIN', 'MANAGER']}>
                  <Layout>
                    <Lazy><ProjectsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:id" element={
                <ProtectedRoute requiredRole={['ADMIN', 'MANAGER']}>
                  <Layout>
                    <Lazy><ProjectDetailPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/timesheets" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><TimesheetsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/timesheets/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><TimesheetDetailPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/timesheets/:id/edit" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><TimesheetEditPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/salaries" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><SalariesPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/salaries/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><SalaryDetailPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />



              <Route path="/approvals/timesheets" element={
                <ProtectedRoute requiredRole={['ADMIN', 'MANAGER']}>
                  <Layout>
                    <Lazy><ApprovalsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/approvals/salaries" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><SalaryDraftsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/employer-cost" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><EmployerCostPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/approvals/:id" element={
                <ProtectedRoute requiredRole={['ADMIN', 'MANAGER']}>
                  <Layout>
                    <Lazy><ApprovalDetailPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/reports" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><ReportsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/users" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><UsersPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/settings" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><SettingsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/notifications" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><NotificationsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/partner-map" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><PartnerMapPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/benefits" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><BenefitsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/benefit-approvals" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><BenefitApprovalsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/benefit-rewards" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><BenefitRewardsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/allowances" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><AllowancesPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/expenses" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><ExpensesPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/expense-approvals" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><ExpenseApprovalsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/performance" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><PerformancePage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/documents" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><DocumentsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/documents/sign/:assignmentId" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><DocumentSignPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/documents/templates" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><DocumentTemplatesPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/documents/signatures" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><DocumentSignatureApprovalsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/my-goals" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><MyGoalsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/goals" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><GoalsAdminPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/goals/users/:userId" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><AdminGoalDetailPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/performance" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><PerformanceAdminPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/level-maps" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><LevelMapsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/bonus-pools" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><BonusPoolsListPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/bonus-pools/:id" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><BonusPoolDetailPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/bonus-pool" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><MyBonusPoolPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/bonus-pool/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><MyBonusPoolDetailPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/office" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><OfficePage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/announcements" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><AnnouncementsPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/announcements" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><AnnouncementAdminPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/wiki" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><WikiHomePage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              {/* /wiki/new 必須排在 /wiki/:slug 之前，否則會被當成一份叫 new 的文件 */}
              <Route path="/wiki/new" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><WikiEditPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/wiki/:slug" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><WikiArticlePage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/wiki/:slug/edit" element={
                <ProtectedRoute>
                  <Layout>
                    <Lazy><WikiEditPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/wiki" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Lazy><WikiAdminPage /></Lazy>
                  </Layout>
                </ProtectedRoute>
              } />

              {/* 404 Page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>

            {/* PWA Components */}
            <OfflineIndicator />
            <InstallPrompt />
          </div>
              </Router>
                </LevelMapProvider>
              </OfficePresenceProvider>
            </TimerProvider>
          </NotificationProvider>
        </AuthProvider>
      </SystemProvider>
    </ThemeProvider>
    </GoogleOAuthProvider>
  )
}

export default App
